import {startCase} from 'es-toolkit';
import {action, computed, makeObservable, observable} from 'mobx';

import type {BNCoachFormsReadParams} from '@yourcoach/shared/api/types/bnrks/methods';
import type {
  CoreClientArticlesReadParams,
  CoreCoachArticlesReadParams,
} from '@yourcoach/shared/api/types/core/methods';
import type {UserApiModel} from '@yourcoach/shared/api/user';

import {apiRequest} from '@yourcoach/shared/api/utils/apiRequest';
import {conferenceFetch} from '@yourcoach/shared/services/entity-action/conference/conference.fetch';
import {courseFetch} from '@yourcoach/shared/services/entity-action/course/course.fetch';
import {programFetch} from '@yourcoach/shared/services/entity-action/program/program.fetch';
import {taskFetch} from '@yourcoach/shared/services/entity-action/task/task.fetch';
import {getCourseLabel} from '@yourcoach/shared/utils/entity/course';
import {getEntityTypeById} from '@yourcoach/shared/utils/entity/getEntityType';
import {isEntityId} from '@yourcoach/shared/utils/entity/isEntityId';
import {isIamCoach} from '@yourcoach/shared/utils/entity/isIamCoach';

import type {
  ArticleBase,
  ConferenceBase,
  CourseBase,
  FormBase,
  ProgramBase,
  SupportedEntity,
  TaskBase,
  UserBase,
} from '../../types';

type EmptyCourse = {title: string};

export class Crumb {
  _label: string | undefined;
  _path: string[];

  constructor(path: string[], slug: string) {
    this.setPath(path);
    this.setLabel(slug);
    makeObservable(this, {
      _label: observable,
      _path: observable,
      label: computed,
      path: computed,
      setLabel: action,
      setPath: action,
    });
  }

  get label() {
    return startCase(this._label || '');
  }

  get path() {
    return '/' + this._path.join('/');
  }

  static async build(path: string[]) {
    const value = path.slice(-1)[0]!;

    let slug = value;

    if (isEntityId(value)) {
      const entity = await Crumb.getEntityData(value, path);

      if (entity) {
        const entityType = getEntityTypeById(value) ?? 'unknown';

        slug = Crumb.getSlugFromEntity(entity, entityType);
      }
    }

    return new Crumb(path, slug);
  }

  setLabel(label: string) {
    if (label) {
      this._label = label;
    }
  }

  setPath(path: string[]) {
    this._path = path;
  }

  private static async getEntityData(
    entityId: string,
    path: string[],
  ): Promise<EmptyCourse | SupportedEntity> {
    const entityType = getEntityTypeById(entityId);
    const params = {_id: entityId};

    switch (entityType) {
      case 'coach_task':
      case 'task':
        return await taskFetch({asClient: {params}, asCoach: {params}});
      case 'conference':
        return await conferenceFetch({params});
      case 'course': {
        const course = await courseFetch({params});

        return course ?? {title: 'course not found'};
      }
      case 'user':
        try {
          const request = apiRequest({
            method: 'public.profile.read',
            params: {
              _id: entityId,
            },
          });
          const result: {user: UserApiModel} = await request;

          return result.user;
        } catch (error) {
          return {name: 'User not found'} as UserApiModel;
        }
      case 'program': {
        const hasToBeCoachId = path.slice(-2)[0];

        return await programFetch({
          params: {...params, coach_id: hasToBeCoachId},
        });
      }
      case 'form':
        try {
          const request = apiRequest<BNCoachFormsReadParams>({
            method: 'coach.forms.read',
            params: {
              _id: entityId,
            },
          });
          const result: {form: any} = await request;

          return result.form;
        } catch (error) {
          return {title: 'Form not found'};
        }
      case 'article':
        try {
          const request = apiRequest<
            CoreClientArticlesReadParams | CoreCoachArticlesReadParams
          >({
            method: isIamCoach()
              ? 'coach.articles.read'
              : 'client.articles.read',
            params: {
              _id: entityId,
            },
          });
          const result: {article: any} = await request;

          return result.article;
        } catch (error) {
          return {title: 'Article not found'};
        }
      default:
        throw 'Crumb.store: unsupported entity type: ' + entityType;
    }
  }

  private static getSlugFromEntity(
    entity: EmptyCourse | SupportedEntity,
    entityType: string,
  ): string {
    switch (entityType) {
      case 'article':
        return (entity as ArticleBase).title;
      case 'coach_task':
      case 'task':
        return (entity as TaskBase).title;
      case 'conference':
        return (entity as ConferenceBase).title;
      case 'course': {
        const course = {...entity} as CourseBase;

        if (!course.start_date) {
          return (course as unknown as EmptyCourse).title;
        }

        return getCourseLabel(course);
      }
      case 'form':
        return (entity as FormBase).title;
      case 'program':
        return (entity as ProgramBase).slug ?? (entity as ProgramBase).title;
      case 'user':
        return (entity as UserBase).slug ?? (entity as UserBase).name;
      default:
        throw 'Crumbs store: unsupported entity type: ' + entityType;
    }
  }
}
